import { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Tab } from 'react-bootstrap'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Tabs } from '../../../components/Tabs/Tabs'
import { IntegrationAboutTab } from './IntegrationAboutTab'
import { IntegrationManagementHeader } from './IntegrationManagementHeader'
import { ClickNavigation } from '../../../components/NavigationWithSideBar/ClickNavigation'
import { IntegrationAdminSettingsContainer } from './AdminSettings/AdminSettings'
import { IntegrationAdminSettingsCustom } from './AdminSettings/IntegrationAdminSettingsCustom'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetIntegrationAdminSettings } from './AdminSettings/hook'
import { useIntegrationSpecificAdminSections } from './AdminSettings/IntegrationSpecificAdminSettings'
import { IntegrationServiceId } from '../../../common/integrations'
import { IntegrationSpecificSetting } from './IntegrationSpecificSettings/IntegrationSpecificSetting'

export const IntegrationManagementTabs: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { portalRef } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()

  const { serviceId, activeTab = 'about' } = useParams<{
    serviceId: string
    activeTab?: string
  }>() as { serviceId: IntegrationServiceId; activeTab?: string }

  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId })
  const { currentIntegrationAccountConfig } = useGetIntegrationAdminSettings(serviceId)

  const hasIntegration = useMemo(() => {
    if (!userData || !userData.integrations) {
      return false
    }
    return !!userData.integrations.some((i) => i.serviceId === serviceId)
  }, [userData, serviceId])

  const hasIntegrationConnectScope = hasScope('**', 'integration:connect', ['integration:admin'])

  const hasAdminScope = hasIntegrationConnectScope && hasScope('**', 'account:update', ['account:admin'])

  const yourSettingsSections = useMemo(() => IntegrationSpecificSetting({ serviceId }), [serviceId])
  const showYourSettingsTab = yourSettingsSections.length > 0

  const adminSettingsSections = useIntegrationSpecificAdminSections(serviceId)
  const showAdminTab = !!(adminSettingsSections && adminSettingsSections.length > 0)

  const canAccessSettings = showYourSettingsTab && hasIntegrationConnectScope && hasIntegration

  const canAccessAdmin = showAdminTab && hasAdminScope

  useEffect(() => {
    if (isUserLoading) return

    const fallbackUrl = `/account/integration-settings/${serviceId}/about`

    if (activeTab === 'settings' && !canAccessSettings) {
      navigate(fallbackUrl, { replace: true })
    }

    if (activeTab === 'admin' && !canAccessAdmin) {
      navigate(fallbackUrl, { replace: true })
    }
  }, [activeTab, canAccessSettings, canAccessAdmin, isUserLoading, serviceId, navigate])

  const handleTabSelect = (key: string | null) => {
    if (key && key !== activeTab) {
      navigate(
        {
          pathname: `/account/integration-settings/${serviceId}/${key}`,
          search: searchParams.toString(),
        },
        { replace: false },
      )
    }
  }

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  return (
    <>
      {headerContainer ? createPortal(<IntegrationManagementHeader />, headerContainer) : null}

      <Tabs className='mb-6' id='integration-management-tabs' defaultActiveKey={activeTab} activeKey={activeTab} onSelect={handleTabSelect}>
        <Tab eventKey='about' title='About'>
          <IntegrationAboutTab serviceId={serviceId} />
        </Tab>

        {showYourSettingsTab && (
          <Tab eventKey='settings' title='Your Settings' disabled={!canAccessSettings}>
            {activeTab === 'settings' && canAccessSettings && (
              <ClickNavigation sections={yourSettingsSections} defaultActiveSectionId={yourSettingsSections[0].id} />
            )}
          </Tab>
        )}

        {showAdminTab && (
          <Tab eventKey='admin' title='Admin' disabled={!hasAdminScope}>
            {activeTab === 'admin' &&
              canAccessAdmin &&
              (!currentIntegrationAccountConfig?.isCustom ? (
                <IntegrationAdminSettingsContainer sections={adminSettingsSections} serviceId={serviceId} />
              ) : (
                <IntegrationAdminSettingsCustom serviceId={serviceId} />
              ))}
          </Tab>
        )}
      </Tabs>
    </>
  )
}
